<template>
  <div class="w-6/12 mx-auto">
    <div>
      <div>
        <label class="font-semibold text-md text-grey-700">
          {{ $t('frontend_groovemail_leads_import_multiple_leads_question_add_website_url') }}
        </label>
      </div>
      <div>
        <label class="font-semibold text-sm text-grey-600">
          {{ $t('frontend_groovemail_leads_import_multiple_leads_label_owner_of_added_websites') }}.
        </label>
      </div>
      <div class="flex mt-2" :class="{ 'mb-4': this.$v.websiteUrl.$invalid && this.$v.websiteUrl.$error }">
        <div class="w-11/12 flex mr-2">
          <text-input
            variant="base"
            v-model="websiteUrl"
            :validator="$v.websiteUrl"
            :placeholder="$t('frontend_groovemail_leads_import_multiple_leads_enter_website_url')"
          />
        </div>
        <div class="w-1/12">
          <gd-button
            class="h-10"
            variant="primary"
            size="sm"
            @click="addWebsiteUrl"
            >{{ $t('frontend_add') }}</gd-button
          >
        </div>
      </div>
      <p
        v-if="stepSaved &&
        (!localModel.websiteUrls ||
          (localModel.websiteUrls && localModel.websiteUrls.length === 0))"
        class="text-sm text-danger"
      >
        {{ $t('frontend_groovemail_leads_import_multiple_leads_question_add_website_url_attribute') }}
      </p>
    </div>
    <div class="flex pb-2 mt-2">
      <div class="w-full text-left">
        <div class="flex flex-wrap">
          <div
            v-for="(url, urlIndex) in localModel.websiteUrls"
            :key="urlIndex"
            class="rounded bg-primary text-primary-invert mr-1 mb-1"
          >
            <div class="text-sm px-2 py-1 float-left">
              {{ url.mainUrl }}
            </div>
            <div
              class="cursor-pointer px-2 py-1 pull-right hover:text-danger"
              @click="deleteWebsiteUrl(urlIndex)"
            >
              <i class="fas fa-times"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { required, url } from 'vuelidate/lib/validators';
import showAlert from '@/components/sweet-alert-popup.js';
import themeConfig from '@/theme/themeConfig';

export default {
  name: 'step2',
  props: {
    model: {
      required: true,
    },
  },

  data() {
    return {
      stepSaved: false,
      websiteUrl: '',
      localModel: this.model,
    };
  },

  validations: {
    websiteUrl: {
      url,
      required,
    },
  },

  methods: {
    validate() {
      this.stepSaved = true;
      let isValid = false;
      if (
        this.localModel.websiteUrls &&
        this.localModel.websiteUrls.length
      ) {
        isValid = true;
      }
      this.$emit('on-validate', isValid);
      return isValid;
    },

    /**
     * Adds a new website URL to the local model.
     * Validates the website URL and checks if the domain is safe.
     * If the domain is not safe, shows an error message and does not add the URL.
     * @returns {boolean} - Returns false if the domain is not safe, otherwise returns undefined.
    */
    async addWebsiteUrl() {
      // Validate the website URL
      this.$v.websiteUrl.$touch();
      const urlValid = !this.$v.websiteUrl.$invalid;

      // If the URL is valid, extract the domain name and check if it is safe
      if (urlValid) {
        const urlSplitArray = this.websiteUrl.split("/");
        let domainName = urlSplitArray[2];
        if (urlSplitArray[2].includes("www") && urlSplitArray[2].indexOf("www.") === 0) {
          domainName = urlSplitArray[2].substring(4);
        }
        this.$emit('showLoader');
        try {
          const domainCheckPayload = {
            domain: domainName
          };
          const domainCheckRes = await axios.post('/import-safe-domain-check', domainCheckPayload);

          // If the domain is not safe, show an error message and return false
          if (!domainCheckRes.data.safe) {
            this.showErrorForDomainCheck(domainName);
            this.$emit('hideLoader');
            return false;
          }
        } catch (err) {
          // If there is an error checking the domain, show an error message and return false
          this.showErrorForDomainCheck(domainName);
          this.$emit('hideLoader');
          return false;
        }

        // If the domain is safe, add the website URL to the local model and reset the input field
        this.$emit('hideLoader');
        this.localModel.websiteUrls.push({
          urlLink: this.websiteUrl,
          email: '',
          verifyOtp: '',
          domain: domainName,
          verified: 0,
          mainUrl: `${urlSplitArray[0]}//${domainName}`,
          sendDisabled: true,
          verificationSendMailCount: 0,
          id: '',
        });
        this.websiteUrl = '';
        this.$v.websiteUrl.$reset();
      }

      // Return undefined to make it clear that the function does not return anything in most cases
      return undefined;
    },

    showErrorForDomainCheck(domainName) {
      showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${ this.$t('frontend_groovemail_import_leads_this_domain_is_restricted') } (${ domainName }) ${ this.$t('frontend_groovemail_import_leads_this_domain_is_restricted_for_website') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t(
          'frontend_close'
        ),
      });
    },

    deleteWebsiteUrl(index) {
      if (index > -1) {
        this.localModel.websiteUrls.splice(index, 1);
      }
    },
  },

  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },
  },
};
</script>